import { DatePipe, DecimalPipe } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  ApplicationConfig,
  ENVIRONMENT_INITIALIZER,
  importProvidersFrom,
  inject,
  provideZoneChangeDetection,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { JwtModule } from '@auth0/angular-jwt';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule, provideRouterStore, routerReducer } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideUserIdleConfig } from 'angular-user-idle';
import { provideMarkdown } from 'ngx-markdown';
import { provideToastr } from 'ngx-toastr';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { AuthModule } from './generated/auth/auth.module';
import { RequestInterceptor } from './interceptors/request.interceptor';
import { ConfirmDialogService } from './shared/dialogs/confirm-dialog/confirm-dialog.service';
import { SuccessDialogService } from './shared/dialogs/success-dialog/success-dialog.service';
import { AuthGuard } from './shared/guards/auth.guards';
import { UITitleStoreModule } from './shared/ui/client-title/store/store.module';
import { UICountryStoreModule } from './shared/ui/country/store/store.module';
import { UIGenderStoreModule } from './shared/ui/gender/store/store.module';
import { UIIdTypeStoreModule } from './shared/ui/identification-type/store/store.module';
import { ACCESS_TOKEN } from './shared/utils/app.const';
import { UIAssetManagersStoreModule } from './store/asset-managers';
import { AuthStoreModule } from './store/auth/auth.module';
import { UIBankAccountTypesStoreModule } from './store/bank-account-types/bank-account-types.module';
import { UIBankWithBranchesStoreModule } from './store/bank-with-branches';
import { UIBranchCodesStoreModule } from './store/branch-codes';
import { CommonStoreModule } from './store/common/common.module';
import { UIOnboardingStoreModule } from './store/direct-onboarding';
import { ExternalNotificationStoreModule } from './store/external-notification';
import { UIIndustriesOrSectorsStoreModule } from './store/industries-or-sectors';
import { UIInvestmentObjectivesStoreModule } from './store/investment-objectives/investment-objectives.module';
import { UIMaritalStatusStoreModule } from './store/marital-status';
import { UIOnboardingInfoStoreModule } from './store/onboarding-info';
import { UIOccupationStoreModule } from './store/occupation';
import { UIPartnerCodesStoreModule } from './store/partner-codes/partner-codes.module';
import { UIPortfolioManagersStoreModule } from './store/portfolio-managers/portfolio-managers.module';
import { UIPortfolioTypesStoreModule } from './store/portfolio-types/portfolio-types.module';
import { UIPortfoliosStoreModule } from './store/portfolios/portfolios.module';
import { UIProductsStoreModule } from './store/products/products.module';
import { ProposalStoreModule } from './store/proposal/proposal.module';
import { UIProvincesStoreModule } from './store/provinces';
import { UIRelationshipTeamStoreModule } from './store/relationship-team/relationship-team.module';
import { UIRiskTolerancesStoreModule } from './store/risk-tolerances/risk-tolerances.module';

import 'prismjs';
import 'prismjs/components/prism-typescript.min.js';
import 'prismjs/plugins/line-highlight/prism-line-highlight.js';
import 'prismjs/plugins/line-numbers/prism-line-numbers.js';
import { UIInternationalCurrenciesStoreModule } from './store/international-currencies';
import { AdvisorOnboardingStoreModule } from './store/advisor-onboarding';

export function tokenGetter() {
  return sessionStorage.getItem(ACCESS_TOKEN);
}

export function initializeConfirmDialogService() {
  return () => {
    inject(ConfirmDialogService);
  };
}
export function initializeSuccessDialogService() {
  return () => {
    inject(SuccessDialogService);
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    DatePipe,
    DecimalPipe,
    provideAnimations(),
    provideToastr(),
    provideMarkdown(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
      }),
    ),
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RequestInterceptor,
      multi: true,
    },
    {
      provide: ENVIRONMENT_INITIALIZER,
      useFactory: initializeConfirmDialogService,
      deps: [MatDialog],
      multi: true,
    },
    {
      provide: ENVIRONMENT_INITIALIZER,
      useFactory: initializeSuccessDialogService,
      deps: [MatDialog],
      multi: true,
    },

    provideUserIdleConfig({
      idle: environment.sessionTimeOutSeconds,
      timeout: environment.sessionTimeOutAutocloseSeconds,
      ping: environment.sessionTimeOutPingSeconds,
    }),
    provideHttpClient(),
    importProvidersFrom(
      HttpClient,
      AuthGuard,
      AuthModule.forRoot({ rootUrl: environment.wealthOnlineApiUrl }),
      JwtModule.forRoot({
        config: {
          tokenGetter: tokenGetter,
        },
      }),

      // Store
      StoreModule.forRoot({
        router: routerReducer,
      }),
      EffectsModule.forRoot(),
      StoreRouterConnectingModule.forRoot(),
      ExternalNotificationStoreModule,

      // NGRX Store
      AuthStoreModule,
      CommonStoreModule,
      ProposalStoreModule,
      AdvisorOnboardingStoreModule,
      UIAssetManagersStoreModule,
      UIBankAccountTypesStoreModule,
      UIBankWithBranchesStoreModule,
      UIBranchCodesStoreModule,
      UICountryStoreModule,
      UIGenderStoreModule,
      UIIdTypeStoreModule,
      UIIndustriesOrSectorsStoreModule,
      UIInternationalCurrenciesStoreModule,
      UIInvestmentObjectivesStoreModule,
      UIMaritalStatusStoreModule,
      UIOnboardingInfoStoreModule,
      UIOnboardingStoreModule,
      UIOccupationStoreModule,
      UIPartnerCodesStoreModule,
      UIPortfolioManagersStoreModule,
      UIPortfoliosStoreModule,
      UIPortfolioTypesStoreModule,
      UIProductsStoreModule,
      UIProvincesStoreModule,
      UIRelationshipTeamStoreModule,
      UIRiskTolerancesStoreModule,
      UITitleStoreModule,
    ),
    provideStoreDevtools({
      maxAge: 25, // Retains last 25 states
      logOnly: true, // environment.production,
      autoPause: true, // Pauses recording actions and state changes when the extension window is not open
      trace: true, //  If set to true, will include stack trace for every dispatched action, so you can see it in trace tab jumping directly to that part of code
      traceLimit: 75, // maximum stack trace frames to be stored (in case trace option was provided as true)
    }),
    provideRouterStore(),
  ],
};
